/**
 * State glyphs and icons, lifted from the artboards' SVG sprite (§11.3).
 *
 * Inline SVG on a 16px grid. State glyphs are 2px stroke so they stay legible at 12px;
 * everything else is 1.4–1.6.
 *
 * **No icon font and no emoji anywhere in the product.** An audit record may end up
 * attached to a contract.
 */

import type { ReactNode, SVGProps } from 'react';

export type GlyphProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'children'> & {
  size?: number;
};

function Glyph({ size = 14, children, ...rest }: GlyphProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{ flex: '0 0 auto', ...rest.style }}
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ── the six item states ───────────────────────────────────────────────────── */

export const VerifiedGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M3 8.6l3.2 3.2L13 4.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Glyph>
);

export const PartialGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <circle cx="8" cy="8" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 2.8a5.2 5.2 0 010 10.4z" fill="currentColor" />
  </Glyph>
);

export const RejectedGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Glyph>
);

export const PendingGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <circle cx="8" cy="8" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
  </Glyph>
);

export const NotApplicableGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path d="M3.4 8h9.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </Glyph>
);

export const WaivedGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M8 2l4.6 1.8v4.3c0 2.7-1.9 5.2-4.6 6.1-2.7-.9-4.6-3.4-4.6-6.1V3.8z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M8 5.8v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11" r=".85" fill="currentColor" />
  </Glyph>
);

/* ── provenance ────────────────────────────────────────────────────────────── */

export const InheritedGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M4 2.8v5.4a2 2 0 002 2h5.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M9.6 7.8l2.6 2.4-2.6 2.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Glyph>
);

export const AutoCheckGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path d="M9.2 1.8L3.8 9.2h3.4l-.8 5 5.6-7.6H8.4z" fill="currentColor" />
  </Glyph>
);

export const FreshnessGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M13 8a5 5 0 11-1.6-3.7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M13.4 1.8v3.2h-3.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Glyph>
);

/* ── general ───────────────────────────────────────────────────────────────── */

export const EvidenceGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M10.5 5.5L6 10a2 2 0 102.8 2.8l4.2-4.2a3.5 3.5 0 10-5-5L3.5 8.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </Glyph>
);

export const RepositoryGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <circle cx="4.5" cy="4" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4.5" cy="12" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="11.5" cy="6.5" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M4.5 5.8v4.4M6.2 5.6c2.4.3 3.5 1 3.7 2.6-.2 1.7-1.7 2.4-4 2.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </Glyph>
);

export const FindingGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M4 14V3h8l-1.5 3L12 9H4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </Glyph>
);

export const CalendarGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2.5 6.5h11M5.5 2v2.5M10.5 2v2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </Glyph>
);

export const WarningGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path d="M8 2.2l6 11.3H2z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M8 6.6v3.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11.6" r=".85" fill="currentColor" />
  </Glyph>
);

export const LockGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <rect x="3.5" y="7" width="9" height="6.5" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5.6 7V5.4a2.4 2.4 0 014.8 0V7" fill="none" stroke="currentColor" strokeWidth="1.4" />
  </Glyph>
);

export const PenGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M2.5 13.5l1-3.2 7-7 2.2 2.2-7 7z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </Glyph>
);

export const GaugeGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M2.5 12a6 6 0 1111 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M8 12V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </Glyph>
);

export const FolderGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M2 4.5h4l1.2 1.5H14v6H2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </Glyph>
);

export const ChevronGlyph = (props: GlyphProps) => (
  <Glyph {...props}>
    <path
      d="M6 4l4 4-4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Glyph>
);
